"use client";

import { c as _c } from "react/compiler-runtime";
import { releaseSavedEnrollment } from "../lib/saved-enrollment";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RiArrowRightLine, RiCloseLine, RiLoader4Line, RiShoppingCartLine } from "react-icons/ri";
import { usePathname } from "next/navigation";
import { useProgressNavigation } from "@/components/progress-bar";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import { invalidateCompletedCheckout } from "@/lib/billing/invalidate-completed-checkout";
import { formatCurrency, formatName } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { ChargePaymentUpdate } from "@/types/payment";
import { CheckoutSnapshot, dismissParkedCart, pickFreshSnapshots, useCheckoutSnapshotStore } from "./checkout-snapshot-store";

// Console-wide docked pill stack: one pill per parked cart, most-recently
// updated on top. Clicking a pill deep-links into the checkout for that
// specific patient so the right snapshot rehydrates. Reads only the
// snapshot store — no checkout providers.
export function ActiveCheckoutPill() {
  const $ = _c(14);
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const hasPaymentsModule = useModule("PAYMENTS");
  const snapshotsByPatientId = useCheckoutSnapshotStore(_temp);
  const snapshots = pickFreshSnapshots(snapshotsByPatientId, currentUser?.id);
  const [navigatingPatientId, setNavigatingPatientId] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setNavigatingPatientId(null);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== pathname) {
    t1 = [pathname];
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== queryClient) {
    t2 = function handleMessage(message) {
      const paidChargeId = (message.data as ChargePaymentUpdate)?.chargeId;
      if (!paidChargeId) {
        return;
      }
      const parked = useCheckoutSnapshotStore.getState().snapshotsByPatientId;
      for (const snapshot of Object.values(parked)) {
        if (snapshot.charge.id === paidChargeId || snapshot.focusedChargeId === paidChargeId) {
          invalidateCompletedCheckout(queryClient, {
            patientId: snapshot.patient.id,
            charges: [snapshot.charge]
          });
          dismissParkedCart(snapshot.patient.id);
        }
      }
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleMessage = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [AlertType.CHARGE_PAYMENT_CREATED];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useWebSocketCallbackSubscription(t3, handleMessage, true, "ActiveCheckoutPill");
  if (pathname.includes("/billing/check-out")) {
    return null;
  }
  if (!hasPaymentsModule || !hasPermission(currentUser, PERMISSIONS.BILLING_PAYMENTS_PROCESS)) {
    return null;
  }
  if (snapshots.length === 0) {
    return null;
  }
  const hasMessaging = tenant?.enabledModules.some(_temp2) ?? false;
  const onChatPage = pathname.includes("/comms/chat") || pathname.includes("/ask-decoda");
  const messagesBarVisible = hasMessaging && !onChatPage;
  const t4 = messagesBarVisible ? "bottom-[60px]" : "bottom-4";
  let t5;
  if ($[6] !== t4) {
    t5 = cx("fixed right-4 z-40 flex flex-col-reverse items-end gap-2", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = "active-checkout-pill-stack";
  let t7;
  if ($[8] !== navigateWithProgress || $[9] !== navigatingPatientId) {
    t7 = snapshot_0 => <CheckoutPill key={snapshot_0.patient.id} snapshot={snapshot_0} isNavigating={navigatingPatientId === snapshot_0.patient.id} onResume={() => {
      setNavigatingPatientId(snapshot_0.patient.id);
      const params = snapshot_0.focusedChargeId ? `patientId=${snapshot_0.patient.id}&chargeId=${snapshot_0.focusedChargeId}` : `patientId=${snapshot_0.patient.id}`;
      navigateWithProgress(`/billing/check-out?${params}`);
    }} onClear={() => {
      if (!snapshot_0.focusedChargeId && !snapshot_0.draftChargeId) {
        releaseSavedEnrollment(snapshot_0.charge);
      }
      dismissParkedCart(snapshot_0.patient.id);
    }} />;
    $[8] = navigateWithProgress;
    $[9] = navigatingPatientId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = snapshots.map(t7);
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <div className={t5} data-testid={t6}>{t8}</div>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
function _temp2(module) {
  return module === "TEXTING" || module === "CALLING";
}
function _temp(state) {
  return state.snapshotsByPatientId;
}
function CheckoutPill(t0) {
  const $ = _c(37);
  const {
    snapshot,
    isNavigating,
    onResume,
    onClear
  } = t0;
  const itemCount = snapshot.charge.items.length;
  const total = snapshot.charge.total ?? 0;
  let t1;
  if ($[0] !== snapshot.patient.firstName || $[1] !== snapshot.patient.lastName) {
    t1 = formatName(snapshot.patient.firstName, snapshot.patient.lastName);
    $[0] = snapshot.patient.firstName;
    $[1] = snapshot.patient.lastName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const patientName = t1;
  const t2 = snapshot.patient.id;
  const t3 = `Discard ${patientName}'s parked cart`;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("group/clear flex size-9 items-center justify-center rounded-lg transition", "bg-primary/10 text-primary", "hover:bg-destructive/10 hover:text-destructive", "disabled:cursor-not-allowed disabled:opacity-70", "disabled:hover:bg-primary/10 disabled:hover:text-primary");
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isNavigating) {
    t5 = isNavigating ? <RiLoader4Line className="size-5 animate-spin" aria-hidden={true} /> : <><RiShoppingCartLine className="size-5 group-hover/clear:hidden" aria-hidden={true} /><RiCloseLine className="hidden size-5 group-hover/clear:block" aria-hidden={true} /></>;
    $[4] = isNavigating;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isNavigating || $[7] !== onClear || $[8] !== t3 || $[9] !== t5) {
    t6 = <button type="button" onClick={onClear} disabled={isNavigating} aria-label={t3} data-testid="active-checkout-pill-clear" className={t4}>{t5}</button>;
    $[6] = isNavigating;
    $[7] = onClear;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `Resume ${patientName}'s checkout`;
  const t8 = isNavigating ? "Opening checkout\u2026" : "Checkout in progress";
  let t9;
  if ($[11] !== t8) {
    t9 = <span className="block text-sm font-medium text-foreground">{t8}</span>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = itemCount === 1 ? "item" : "items";
  let t11;
  if ($[13] !== t10) {
    t11 = <span>{t10}</span>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== total) {
    t12 = total > 0 && ` · ${formatCurrency(total)}`;
    $[15] = total;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <span>{t12}</span>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== itemCount || $[20] !== patientName || $[21] !== t11 || $[22] !== t13) {
    t14 = <span className="block text-xs text-muted-foreground">{patientName} · {itemCount}{" "}{t11}{t13}</span>;
    $[19] = itemCount;
    $[20] = patientName;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t9) {
    t15 = <span>{t9}{t14}</span>;
    $[24] = t14;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <RiArrowRightLine className="size-4 text-foreground-muted" aria-hidden={true} />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== isNavigating || $[29] !== onResume || $[30] !== t15 || $[31] !== t7) {
    t17 = <button type="button" onClick={onResume} disabled={isNavigating} aria-label={t7} data-testid="active-checkout-pill-resume" className="flex flex-1 items-center gap-3 text-left disabled:opacity-70">{t15}{t16}</button>;
    $[28] = isNavigating;
    $[29] = onResume;
    $[30] = t15;
    $[31] = t7;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== snapshot.patient.id || $[34] !== t17 || $[35] !== t6) {
    t18 = <div data-testid="active-checkout-pill" data-patient-id={t2} className="flex items-center gap-3 rounded-xl border bg-background py-2.5 pl-3.5 pr-3 text-left shadow-lg transition hover:border-primary hover:shadow-xl">{t6}{t17}</div>;
    $[33] = snapshot.patient.id;
    $[34] = t17;
    $[35] = t6;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
}